import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, DatePicker, Form, message, Modal, Spin, Tabs } from 'antd';
import { connect } from 'react-redux';
import moment from 'moment';
import cls from 'classnames';
import { fetchCurrentSysTime } from '@/api/order';
import { fetchUpdateCouponDate } from '@/api/guest';
import { ItabItem, renderIntlLabel } from '@/utils';
import { DateType } from '@/utils/date';
import { CouponTabStatus } from '@/config';
import ExchangeCodeModal, { IExchangeCodeRef } from '@/components/ExchangeCodeModal';
import PresentedCouponDrawer, { imperativeHandle } from '@/components/PresentedCouponDrawer';
import CouponTab from '../CouponTab';

import styles from './index.module.scss';

const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

interface IcouponListProps {
  myRef?: any;
  guestInfo?: any;
  basicCardInfo?: any;
  dataSource?: Array<any>;
  loading?: boolean;
  onSave?: (...args) => void;
  onSortChange?: (...args) => void;
  onGiveCouponClose?: () => void;
  onCouponTabChange?: (...args) => void;
}

const CouponList: React.FC<IcouponListProps> = ({
  guestInfo,
  basicCardInfo,
  dataSource = [],
  loading,
  onSave,
  onSortChange,
  onGiveCouponClose,
  onCouponTabChange,
}) => {
  const [selectedKey, setSelectedKey] = useState<string>(CouponTabStatus.CanUse);

  const [modalForm] = Form.useForm();
  const [couponDetail, setCouponDetail] = useState<any>();
  const [open, setOpen] = useState<boolean>(false);
  const [businessDate, setBusinessDate] = useState<any>();
  const [saveLoading, setSaveLoading] = useState<boolean>(false);

  // 1按有效期，2金额
  const [sortType, setSortType] = useState<1 | 2>(1);

  const exchangeRef = useRef<IExchangeCodeRef>();
  const giveAwayRef = useRef<imperativeHandle>();

  const initCurrentBusinessDate = async () => {
    const res = await fetchCurrentSysTime();
    if (res.code === 0) {
      if (res.data?.businessDate) {
        setBusinessDate(moment(res.data?.businessDate));
      } else {
        setBusinessDate(null);
      }
    }
  };

  const handleShowEditModal = async (item) => {
    setCouponDetail(item);
    const { startDate, endDate } = item || {};
    if (startDate && endDate) {
      modalForm.setFieldsValue({ indateRange: [moment(startDate), moment(endDate)] });
    } else {
      modalForm.setFieldsValue({ indateRange: [] });
    }
    setOpen(true);
  };

  const disabledDate: (date: DateType) => boolean = (date) => {
    return date.isBefore(businessDate) || date.isAfter(businessDate?.clone()?.add(1, 'year'));
  };

  const handleCancel = () => {
    setOpen(false);
  };

  // 优惠券日期是否可编辑
  const couponDateEditable = couponDetail && couponDetail.effectTimeEditable === 1;

  const handleOk = async () => {
    if (!couponDateEditable) {
      message.error(renderIntlLabel('guest_msg_cannot_support_modify_indate'));
      return;
    }
    const values = await modalForm.validateFields();
    const { indateRange } = values;
    const params: any = { couponNo: couponDetail?.couponNo, wyuid: couponDetail?.wyuid };
    if (indateRange && indateRange?.length >= 2) {
      params.startDate = moment(indateRange[0])
        .hour(0)
        .minute(0)
        .second(0)
        .millisecond(0)
        .valueOf();
      params.endDate = moment(indateRange[1])
        .hour(23)
        .minute(59)
        .second(59)
        .millisecond(0)
        .valueOf();
    }
    try {
      setSaveLoading(true);
      const res = await fetchUpdateCouponDate(params);
      setSaveLoading(false);
      if (res.code === 0) {
        setOpen(false);
        if (typeof onSave === 'function') {
          onSave(sortType);
        }
      }
    } catch (error) {
      setSaveLoading(false);
    }
  };

  const onSortRadioChange = (val) => {
    setSortType(val);
    if (onSortChange) {
      onSortChange(val);
    }
  };

  const handleTabChange = (val) => {
    setSelectedKey(val);
    if (onCouponTabChange) {
      onCouponTabChange(val);
    }
  };

  useEffect(() => {
    if (selectedKey === CouponTabStatus.CanUse) {
      initCurrentBusinessDate();
    }
  }, [selectedKey]);

  const tabList: Array<ItabItem> = useMemo(() => {
    return [
      {
        key: CouponTabStatus.CanUse,
        tab: renderIntlLabel('guest_coupon_tab_avalible_in_current_store'),
        component: (
          <CouponTab
            sortType={sortType}
            couponTabStatus={CouponTabStatus.CanUse}
            couponList={dataSource}
            onShowEditModal={handleShowEditModal}
            onSortRadioChange={onSortRadioChange}
          />
        ),
      },
      {
        key: CouponTabStatus.CannotUse,
        tab: renderIntlLabel('guest_coupon_tab_unavalible'),
        component: (
          <CouponTab
            couponTabStatus={CouponTabStatus.CannotUse}
            couponList={dataSource}
            onShowEditModal={handleShowEditModal}
          />
        ),
      },
      {
        key: CouponTabStatus.Used,
        tab: renderIntlLabel('guest_coupon_tab_have_been_used'),
        component: <CouponTab couponTabStatus={CouponTabStatus.Used} couponList={dataSource} />,
      },
      {
        key: CouponTabStatus.Expired,
        tab: renderIntlLabel('guest_coupon_tab_have_expired'),
        component: <CouponTab couponTabStatus={CouponTabStatus.Expired} couponList={dataSource} />,
      },
    ];
  }, [dataSource]);

  const handleOpenExchangeCodeModal = () => {
    exchangeRef.current.openModal({
      record: { wyuid: guestInfo?.wyuid, ...basicCardInfo },
      onOk() {
        if (typeof onGiveCouponClose === 'function') {
          onGiveCouponClose();
        }
      },
    });
  };

  const handleOpenGiveCouponDrawer = () => {
    giveAwayRef.current.openDrawer({
      record: { wyuid: guestInfo?.wyuid, ...basicCardInfo },
      onOk() {
        if (typeof onGiveCouponClose === 'function') {
          onGiveCouponClose();
        }
      },
    });
  };

  return (
    <>
      <ExchangeCodeModal ref={exchangeRef} />
      <PresentedCouponDrawer ref={giveAwayRef} />

      <Spin spinning={loading}>
        <Tabs
          activeKey={selectedKey}
          onChange={handleTabChange}
          destroyInactiveTabPane
          tabBarExtraContent={{
            right: (
              <>
                <Button
                  type="ghost"
                  onClick={handleOpenExchangeCodeModal}
                  style={{ marginRight: '16px' }}
                >
                  {renderIntlLabel('guest_action_use_conversion_code')}
                </Button>
                <Button type="ghost" onClick={() => handleOpenGiveCouponDrawer()}>
                  {renderIntlLabel('guest_action_store_coupon')}
                </Button>
              </>
            ),
          }}
        >
          {Array.isArray(tabList)
            ? tabList.map((item) => (
                <TabPane tab={item.tab} key={item.key}>
                  {item.component}
                </TabPane>
              ))
            : null}
        </Tabs>
      </Spin>

      <Modal
        title={renderIntlLabel('guest_title_edit_of_coupon')}
        maskClosable={false}
        open={open}
        onCancel={handleCancel}
        onOk={handleOk}
        forceRender
        okButtonProps={{ loading: saveLoading }}
      >
        <Form form={modalForm}>
          <Form.Item
            label={renderIntlLabel('guest_coupon_label_effective')}
            className={styles.couponDateField}
          >
            <Form.Item
              name="indateRange"
              rules={[
                { required: true, message: renderIntlLabel('guest_msg_please_select_indate') },
              ]}
            >
              <RangePicker
                placeholder={[
                  renderIntlLabel('guest_coupon_placeholder_please_select_date'),
                  renderIntlLabel('guest_coupon_placeholder_please_select_date'),
                ]}
                disabledDate={disabledDate}
                disabled={!couponDateEditable}
              />
            </Form.Item>
            <div
              className={cls(
                styles.dateDisabledText,
                couponDateEditable ? styles.elementHidden : null,
              )}
            >
              {renderIntlLabel('guest_msg_cannot_support_modify_indate')}
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    ...state.guest,
  };
};

export default connect(mapStateToProps, {})(React.memo(CouponList));
